// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import { CopyIcon } from "@radix-ui/react-icons";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import type { FunctionComponent } from "react";
import { toast } from "@/lib/toast";
import { Copy } from "lucide-react";
const Highlighter = dynamic(() => import("./Highlighter"), { ssr: false });

export const CopyCodeFragment: FunctionComponent<{
  codeString: string;
}> = ({ codeString }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(codeString);
    toast("Copied to clipboard", {
      description: "The code has been copied to your clipboard.",
    });
  };

  return (
    <span className="flex w-full rounded-lg border p-2">
      <span className="font-mono flex-1 text-sm pr-6">{codeString}</span>
      <span className="w-5 h-5">
        <CopyIcon
          className="w-5 h-5 rounded-lg p-0.5 text-xs hover:text-foreground text-muted-foreground transition-all"
          onClick={(e) => {
            e.stopPropagation();
            handleCopy();
          }}
        />
      </span>
    </span>
  );
};

interface Props {
  codeString: string;
  language?:
    "yaml" | "shell" | "rego" | "xml" | "json" | "typescript" | "javascript";
  startingLineNumber?: number;
  highlightRegexPattern?: RegExp;
}
const CopyCode: FunctionComponent<Props> = (props) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(props.codeString);
    toast("Copied to clipboard", {
      description: "The code has been copied to your clipboard.",
    });
  };

  // check if we need to highlight some lines
  const { codeStr, highlightLineNumbers } = useMemo(() => {
    // something like /\+\+\+(.+)\+\+\+/gms
    if (!props.highlightRegexPattern)
      return { codeStr: props.codeString, highlightLineNumbers: null };
    // we need the starting and ending line numbers of the match - and remove the special characters just with the match content

    const reg = props.highlightRegexPattern;
    const matches = reg.exec(props.codeString);

    if (matches === null) {
      return { codeStr: props.codeString, highlightLineNumbers: null };
    }

    let str = props.codeString;
    const before = matches[0];
    const withoutRegex = matches[1];
    // get the line number of the match

    // Get the start and end index of the match
    const matchStartIndex = matches.index;
    const matchEndIndex = reg.lastIndex;

    // Split the code string by newline to calculate line numbers
    const codeLines = props.codeString.slice(0, matchStartIndex).split("\n");
    const startLine = codeLines.length + 1; // Line number where the match starts

    // To calculate the end line, we need the end index and split by newline again
    const codeLinesAfterMatch = props.codeString
      .slice(0, matchEndIndex)
      .split("\n");
    const endLine = codeLinesAfterMatch.length - 1; // Line number where the match ends

    return {
      codeStr: str.replace(before, withoutRegex),
      highlightLineNumbers: [startLine, endLine],
    };
  }, [props.codeString, props.highlightRegexPattern]);

  return (
    <span
      style={{
        height: 18 /*padding*/ + codeStr?.split("\n").length * 20,
      }}
      className="relative block w-full overflow-hidden rounded-md border"
    >
      <span className="absolute bottom-0 left-0 right-0 top-0 animate-pulse bg-card" />
      <span className="relative">
        <Highlighter
          startingLineNumber={props.startingLineNumber}
          codeString={codeStr}
          language={props.language}
          startingHighlightLineNumber={
            highlightLineNumbers ? highlightLineNumbers[0] : null
          }
        />
        {highlightLineNumbers && (
          <span
            className="absolute block pointer-events-none left-0 right-0 top-0 z-10 w-full bg-white/10 bg-blend-darken"
            style={{
              top: `${7 + (highlightLineNumbers[0] - 1) * 20}px`,
              height:
                (highlightLineNumbers[1] - highlightLineNumbers[0] + 1) * 20,
            }}
          />
        )}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="absolute cursor-pointer right-1 top-5 z-10 -translate-y-1/2 rounded-lg bg-card p-2 text-xs text-foreground transition-all hover:opacity-80"
      >
        <Copy className="h-4 w-4" />
      </button>
    </span>
  );
};

export default CopyCode;
